import {
	Camera,
	FileDown,
	LayoutTemplate,
	Lightbulb,
	LogIn,
	MoreHorizontal,
	PenLine,
	Redo2,
	Save,
	Share2,
	Undo2,
} from "lucide-react";
import {
	type KeyboardEvent,
	type ReactNode,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import "./BuilderToolbar.css";
import { Link, useNavigate } from "react-router";
import { SaunaLogo } from "../../../components/builder/SaunaLogo";
import { useAuth } from "../../../hooks/useAuth";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useT } from "../../../i18n";
import type { BuilderWorkspace } from "../types";
import { BuilderHistoryMenu } from "./BuilderHistoryMenu";

interface BuilderToolbarProps {
	engineLabel: string;
	projectName: string;
	onProjectNameChange: (name: string) => void;
	canUndo: boolean;
	canRedo: boolean;
	onUndo: () => void;
	onRedo: () => void;
	statusText: string;
	statusHint?: string;
	activeWorkspace: BuilderWorkspace;
	onExportPdf: () => void;
	onRenderPhoto: () => void;
	renderLoading: boolean;
	onShare: () => void;
	onSave: () => void | Promise<void>;
	saving: boolean;
	onOpenTemplates: () => void;
	showTemplates: boolean;
	onOpenInsights: () => void;
}

interface MoreItem {
	id: string;
	label: string;
	icon: ReactNode;
	onSelect: () => void;
	disabled?: boolean;
}

function getPlatformModKey(): string {
	if (typeof navigator === "undefined") return "Ctrl";
	const platform = navigator.platform ?? "";
	return /Mac|iPhone|iPad/i.test(platform) ? "⌘" : "Ctrl";
}

/**
 * One row: the project as the page's title on the left, the edit history in
 * reach, and the plan's actions on the right — Share, Download PDF, Save —
 * with the occasional ones under a "More" menu that floats over the page
 * instead of pushing the builder down.
 */
export function BuilderToolbar({
	engineLabel,
	projectName,
	onProjectNameChange,
	canUndo,
	canRedo,
	onUndo,
	onRedo,
	statusText,
	statusHint,
	activeWorkspace,
	onExportPdf,
	onRenderPhoto,
	renderLoading,
	onShare,
	onSave,
	saving,
	onOpenTemplates,
	showTemplates,
	onOpenInsights,
}: BuilderToolbarProps) {
	const t = useT();
	const navigate = useNavigate();
	const compact = useIsMobile();
	const { user, authAvailable } = useAuth();
	const modKey = getPlatformModKey();

	const saveTitle = !authAvailable
		? t.degraded_saveTitle
		: saving
			? t.toolbar_savingLabel
			: user
				? t.toolbar_saveTitleAuthed
				: t.toolbar_saveTitleAnon;

	const moreItems: MoreItem[] = [
		// On a phone the plan's actions fold in here; on wider screens they
		// sit in the bar and are not repeated.
		...(compact
			? [
					{
						id: "pdf",
						label: t.toolbar_downloadPdf,
						icon: <FileDown size={16} strokeWidth={2} />,
						onSelect: onExportPdf,
					},
					{
						id: "share",
						label: t.toolbar_shareShort,
						icon: <Share2 size={16} strokeWidth={2} />,
						onSelect: onShare,
					},
				]
			: []),
		...(showTemplates && activeWorkspace === "design"
			? [
					{
						id: "templates",
						label: t.templateGallery_openLabel,
						icon: <LayoutTemplate size={16} strokeWidth={2} />,
						onSelect: onOpenTemplates,
					},
				]
			: []),
		...(activeWorkspace === "build"
			? [
					{
						id: "render",
						label: renderLoading ? t.toolbar_rendering : t.toolbar_renderPhoto,
						icon: <Camera size={16} strokeWidth={2} />,
						onSelect: onRenderPhoto,
						disabled: renderLoading,
					},
				]
			: []),
		{
			id: "insights",
			label: t.insights_buttonLabel,
			icon: <Lightbulb size={16} strokeWidth={2} />,
			onSelect: onOpenInsights,
		},
		...(!user && authAvailable
			? [
					{
						id: "signin",
						label: t.nav_signIn,
						icon: <LogIn size={16} strokeWidth={2} />,
						onSelect: () => navigate("/auth"),
					},
				]
			: []),
	];

	return (
		<header className="builder-header builder-toolbar">
			{/* The page's heading for the outline; the project name is what the
			    reader sees as the title. */}
			<h1 className="builder-toolbar__heading">{t.toolbar_buildHeading}</h1>

			<Link to="/" className="builder-toolbar__home" aria-label={t.nav_home}>
				<SaunaLogo size={28} />
			</Link>

			<div className="builder-title">
				<div className="builder-title__row">
					<input
						id="builder-project-name"
						type="text"
						className="builder-title__input"
						value={projectName}
						onChange={(event) => onProjectNameChange(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter" || event.key === "Escape") {
								event.currentTarget.blur();
							}
						}}
						placeholder={t.toolbar_projectPlaceholder}
						aria-label={t.toolbar_projectLabel}
						spellCheck={false}
					/>
					<PenLine
						className="builder-title__pen"
						size={14}
						strokeWidth={2}
						aria-hidden="true"
					/>
				</div>
				<p className="builder-title__meta">
					<span>{engineLabel}</span>
					<span aria-hidden="true"> · </span>
					<span role="status" title={statusHint}>
						{statusText}
					</span>
				</p>
			</div>

			<fieldset
				className="builder-toolbar__edit"
				aria-label={t.toolbar_editGroup}
			>
				<button
					type="button"
					className="builder-icon-btn"
					onClick={onUndo}
					disabled={!canUndo}
					title={`${t.toolbar_undoTitle} (${modKey}+Z)`}
					aria-label={t.toolbar_undoLabel}
				>
					<Undo2 size={16} strokeWidth={2} aria-hidden="true" />
				</button>
				<button
					type="button"
					className="builder-icon-btn"
					onClick={onRedo}
					disabled={!canRedo}
					title={`${t.toolbar_redoTitle} (${modKey}+Shift+Z)`}
					aria-label={t.toolbar_redoLabel}
				>
					<Redo2 size={16} strokeWidth={2} aria-hidden="true" />
				</button>
				{!compact && <BuilderHistoryMenu />}
			</fieldset>

			<div className="builder-toolbar__actions">
				{!compact && (
					<>
						<button
							type="button"
							className="builder-toolbar-btn"
							onClick={onShare}
							title={t.toolbar_shareTitle}
						>
							<Share2 size={15} strokeWidth={2} aria-hidden="true" />
							<span>{t.toolbar_shareShort}</span>
						</button>
						<button
							type="button"
							className="builder-toolbar-btn"
							onClick={onExportPdf}
						>
							<FileDown size={15} strokeWidth={2} aria-hidden="true" />
							<span>{t.toolbar_downloadPdf}</span>
						</button>
					</>
				)}
				<button
					type="button"
					// Only a Save that can save is the loud button.
					className={
						authAvailable
							? "builder-toolbar-btn builder-toolbar-btn--primary"
							: "builder-toolbar-btn"
					}
					onClick={onSave}
					disabled={saving || !authAvailable}
					aria-describedby={
						!authAvailable ? "builder-save-unavailable" : undefined
					}
					aria-label={
						!authAvailable
							? t.degraded_saveLabel
							: saving
								? t.toolbar_savingLabel
								: t.toolbar_saveLabel
					}
					title={saveTitle}
				>
					<Save size={15} strokeWidth={2} aria-hidden="true" />
					<span className="builder-toolbar-btn__label">
						{saving ? t.toolbar_savingLabel : t.toolbar_saveLabel}
					</span>
				</button>
				{/* Hidden, but still the Save button's description (aria-describedby
				    reads hidden elements), so its name stays short. */}
				{!authAvailable && (
					<span id="builder-save-unavailable" hidden>
						{t.degraded_saveTitle}
					</span>
				)}
				<MoreMenu label={t.toolbar_more} items={moreItems} />
			</div>
		</header>
	);
}

/**
 * The occasional actions. A real menu: it floats over the page, focuses its
 * first item, moves with the arrow keys, and closes on Escape, Tab or a click
 * outside — returning focus to its button.
 */
function MoreMenu({ label, items }: { label: string; items: MoreItem[] }) {
	const menuId = useId();
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);

	useEffect(() => {
		if (!open) return;
		itemRefs.current.find((item) => item && !item.disabled)?.focus();
		const handlePointerDown = (event: MouseEvent | TouchEvent) => {
			if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
		};
		document.addEventListener("mousedown", handlePointerDown);
		document.addEventListener("touchstart", handlePointerDown);
		return () => {
			document.removeEventListener("mousedown", handlePointerDown);
			document.removeEventListener("touchstart", handlePointerDown);
		};
	}, [open]);

	const close = (refocus: boolean) => {
		setOpen(false);
		if (refocus) buttonRef.current?.focus();
	};

	const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
		const enabled = itemRefs.current.filter((item): item is HTMLButtonElement =>
			Boolean(item && !item.disabled),
		);
		const index = enabled.indexOf(document.activeElement as HTMLButtonElement);
		const move = (to: number) => {
			event.preventDefault();
			enabled[(to + enabled.length) % enabled.length]?.focus();
		};
		if (event.key === "ArrowDown") move(index + 1);
		else if (event.key === "ArrowUp") move(index - 1);
		else if (event.key === "Home") move(0);
		else if (event.key === "End") move(enabled.length - 1);
		else if (event.key === "Escape") {
			event.preventDefault();
			close(true);
		} else if (event.key === "Tab") close(false);
	};

	return (
		<div className="builder-more" ref={wrapperRef}>
			<button
				type="button"
				ref={buttonRef}
				className="builder-icon-btn builder-more__trigger"
				aria-haspopup="menu"
				aria-expanded={open}
				aria-controls={open ? menuId : undefined}
				aria-label={label}
				title={label}
				onClick={() => setOpen((previous) => !previous)}
				onKeyDown={(event) => {
					if (event.key === "ArrowDown" && !open) {
						event.preventDefault();
						setOpen(true);
					}
				}}
			>
				<MoreHorizontal size={18} strokeWidth={2} aria-hidden="true" />
			</button>
			{open && (
				<div
					id={menuId}
					role="menu"
					aria-label={label}
					className="builder-more__menu"
					onKeyDown={handleMenuKeyDown}
				>
					{items.map((item, index) => (
						<button
							key={item.id}
							ref={(node) => {
								itemRefs.current[index] = node;
							}}
							type="button"
							role="menuitem"
							className="builder-more__item"
							disabled={item.disabled}
							tabIndex={-1}
							onClick={() => {
								close(true);
								item.onSelect();
							}}
						>
							<span className="builder-more__icon" aria-hidden="true">
								{item.icon}
							</span>
							<span>{item.label}</span>
						</button>
					))}
				</div>
			)}
		</div>
	);
}
